import React, { useState } from 'react';
import { 
  TextField, 
  CircularProgress, 
  useMediaQuery, 
  useTheme,
  Modal,
  Box,
  Typography,
  IconButton,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useAuth } from '@context/auth';
import { Button } from '@components/Button';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import CloseIcon from '@material-ui/icons/Close';
import {
  Card,
  ContainerButtons,
  ContainerSignIn,
  Form,
  TitleSignIn,
  LoadingOverlay,
  StyledPaper,
  LogoContainer,
  InputContainer,
  ErrorText,
  LoadingContent,
  LoadingText,
  SubText,
  FooterText,
  BackgroundPattern
} from './styles';
import backgroundImage from '../../assets/prancheta2.png';
import { api } from '../../services/api';

const signInForm = z.object({
  email: z.string().email({ message: 'Por favor, insira um e-mail válido' }),
  password: z.string().min(4, { message: 'A senha deve ter pelo menos 4 caracteres' })
});

type SignForm = z.infer<typeof signInForm>;

// Schema para recuperação de senha
const recoveryFormSchema = z.object({
  login_usuario: z.string().email({ message: 'Por favor, insira um e-mail válido' }),
});

const codeVerificationSchema = z.object({
  codigo: z.string().min(6, { message: 'Código deve ter 6 dígitos' }).max(6),
});

const newPasswordSchema = z.object({
  nova_senha: z.string()
    .min(6, { message: 'A senha deve ter no mínimo 6 caracteres' })
    .regex(/[A-Z]/, { message: 'Deve conter pelo menos uma letra maiúscula' })
    .regex(/[0-9]/, { message: 'Deve conter pelo menos um número' }),
  confirmar_senha: z.string()
}).refine(data => data.nova_senha === data.confirmar_senha, {
  message: "As senhas não coincidem",
  path: ["confirmar_senha"],
});

type RecoveryForm = z.infer<typeof recoveryFormSchema>;
type CodeVerificationForm = z.infer<typeof codeVerificationSchema>;
type NewPasswordForm = z.infer<typeof newPasswordSchema>;

export default function SignIn() {
  const [isLoading, setIsLoading] = useState(false);
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);
  const [isCodeVerificationModalOpen, setIsCodeVerificationModalOpen] = useState(false);
  const [isNewPasswordModalOpen, setIsNewPasswordModalOpen] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset: resetLoginForm
  } = useForm<SignForm>();

  // Forms para recuperação
  const { 
    register: registerRecovery, 
    handleSubmit: handleRecoverySubmit,
    formState: { errors: recoveryErrors },
    reset: resetRecoveryForm 
  } = useForm<RecoveryForm>();

  const { 
    register: registerCode, 
    handleSubmit: handleCodeSubmit,
    formState: { errors: codeErrors },
    reset: resetCodeForm 
  } = useForm<CodeVerificationForm>();

  const { 
    register: registerNewPassword, 
    handleSubmit: handleNewPasswordSubmit,
    formState: { errors: newPasswordErrors },
    watch,
    reset: resetNewPasswordForm 
  } = useForm<NewPasswordForm>();

  const { signIn, messageError } = useAuth();

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  async function handleLogin(data: SignForm) {
    const { email, password } = data;
    setIsLoading(true);
    
    try {
      await signIn(email, password);
    } catch (error) {
      console.error('Erro no login:', error);
      showSnackbar('Erro ao fazer login', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleOpenRecoveryModal = () => {
    setIsRecoveryModalOpen(true);
    resetRecoveryForm();
  };

  const handleCloseRecoveryModal = () => {
    setIsRecoveryModalOpen(false);
    setIsCodeVerificationModalOpen(false);
    setIsNewPasswordModalOpen(false);
    setRecoveryEmail('');
    setResetToken('');
    resetRecoveryForm();
    resetCodeForm();
    resetNewPasswordForm();
  };

const handleSendRecoveryCode = async (data: RecoveryForm) => {
  setIsSendingCode(true);
  
  // Log para debug
  console.log('📤 Enviando para:', data.login_usuario);
  
  try {
    // Tente com uma configuração específica
    const response = await api.post('/ravim/forgot-password', {
      login_usuario: data.login_usuario
    }, {
      validateStatus: function (status) {
        // Aceita tanto 200 quanto 400 como resposta válida
        return (status >= 200 && status < 300) || status === 400;
      }
    });
    
    console.log('📥 Status recebido:', response.status);
    console.log('📥 Dados recebidos:', response.data);
    
    // Se é status 400 (erro do backend)
    if (response.status === 400) {
      // Seu backend retorna {success: false, message: "...", code: 400}
      if (response.data && response.data.message) {
        showSnackbar(response.data.message, 'error');
      } else {
        showSnackbar('Email inválido ou não cadastrado', 'error');
      }
      return;
    }
    
    // Se é status 200 (sucesso)
    if (response.data.success) {
      setRecoveryEmail(data.login_usuario);
      setIsRecoveryModalOpen(false);
      setIsCodeVerificationModalOpen(true);
      showSnackbar('Código enviado para seu e-mail', 'success');
    } else {
      // Status 200 mas success: false (para emails não cadastrados)
      showSnackbar(response.data.message || 'Se o email existir, enviaremos um código', 'info');
    }
    
  } catch (error: any) {
    console.error('Erro catch:', error);
    
    // Para outros erros (rede, 500, etc)
    if (error.response) {
      console.log('Erro response:', error.response.data);
      const message = error.response.data?.message || 
                     error.response.data?.error || 
                     'Erro no servidor';
      showSnackbar(message, 'error');
    } else if (error.request) {
      showSnackbar('Sem conexão com o servidor', 'error');
    } else {
      showSnackbar('Erro: ' + error.message, 'error');
    }
  } finally {
    setIsSendingCode(false);
  }
};

  const handleVerifyCode = async (data: CodeVerificationForm) => {
    setIsVerifyingCode(true);
    try {
      const response = await api.post('/ravim/verify-recovery-code', {
        email: recoveryEmail,
        codigo: data.codigo
      });
      
      if (response.data.success) {
        setResetToken(response.data.token_reset);
        setIsCodeVerificationModalOpen(false);
        setIsNewPasswordModalOpen(true);
        showSnackbar('Código verificado com sucesso', 'success');
      } else {
        showSnackbar(response.data.message || 'Código inválido', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao verificar código:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao verificar código';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handleUpdatePassword = async (data: NewPasswordForm) => {
    setIsUpdatingPassword(true);
    try {
      const response = await api.post('/ravim/reset-password', {
        token: resetToken,
        nova_senha: data.nova_senha
      });
      
      if (response.data.success) {
        showSnackbar('Senha alterada com sucesso!', 'success');
        setTimeout(() => {
          handleCloseRecoveryModal();
        }, 1500);
      } else {
        showSnackbar(response.data.message || 'Erro ao alterar senha', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar senha:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao atualizar senha';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Estilos para os modais
    const modalStyle = {
      position: 'absolute' as 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: isMobile ? '90%' : 400,
      backgroundColor: theme.palette.background.paper,
      borderRadius: '12px',
      boxShadow: theme.shadows[8],
      padding: isMobile ? 16 : 32,
    };

  return (
    <>
      {isLoading && (
        <LoadingOverlay>
          <LoadingContent>
            <CircularProgress size={isMobile ? 40 : 60} style={{ color: '#ffffff' }} />
            <LoadingText>Acessando o Sistema</LoadingText>
            <SubText>Estamos conectando você à plataforma</SubText>
          </LoadingContent>
        </LoadingOverlay>
      )}
      
      <ContainerSignIn>
        <BackgroundPattern />
        <Card>
          <StyledPaper elevation={8}>
            <LogoContainer>
              <img 
                src={backgroundImage} 
                height={isMobile ? 60 : 80} 
                width={isMobile ? 120 : 160} 
                alt="Logo Aryz" 
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  maxWidth: '100%',
                  height: 'auto'
                }}
              />
            </LogoContainer>
            
            <TitleSignIn {...({ isMobile } as any)}>
              Portal Aryz
            </TitleSignIn>
            
            <Form onSubmit={handleSubmit(handleLogin)}>
              <InputContainer>
                <TextField
                  type="email"
                  id="email"
                  fullWidth
                  label="E-mail corporativo"
                  variant="outlined"
                  margin="normal"
                  disabled={isLoading}
                  error={!!errors.email}
                  {...register('email')}
                  size={isMobile ? "small" : "medium"}
                />
                <ErrorText>
                  {errors.email?.message}
                </ErrorText>
              </InputContainer>

              <InputContainer>
                <TextField
                  type="password"
                  id="password"
                  fullWidth
                  label="Senha de acesso"
                  variant="outlined"
                  margin="normal"
                  disabled={isLoading}
                  error={!!errors.password}
                  {...register('password')}
                  size={isMobile ? "small" : "medium"}
                />
                <ErrorText>
                  {errors.password?.message || messageError}
                </ErrorText>
              </InputContainer>

              <ContainerButtons>
                <Button
                  title={isLoading ? "Conectando..." : "Acessar Sistema"}
                  variantstyle={isLoading ? 'DEFAULT' : 'PURPLE'}
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading}
                  style={{
                    minWidth: isMobile ? '180px' : '220px',
                    height: isMobile ? '44px' : '48px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    width: isMobile ? '100%' : 'auto'
                  }}
                />
                <Button
                  title="Esqueci minha senha"
                  variantstyle={isLoading ? 'DEFAULT' : 'LIGHT_PINK'}
                  type="button"
                  onClick={handleOpenRecoveryModal}
                  style={{
                    minWidth: isMobile ? '180px' : '220px',
                    height: isMobile ? '44px' : '48px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    borderRadius: '10px',
                    marginTop: isMobile ? '10px' : '15px',
                    width: isMobile ? '100%' : 'auto',

                  }}
                />
              </ContainerButtons>
            </Form>

            <FooterText {...({ isMobile } as any)}>
              © 2024 Aryz • Sistema de Gestão Integrada
            </FooterText>
          </StyledPaper>
        </Card>
      </ContainerSignIn>

      {/* Modal para recuperação de senha - Email */}
      <Modal
        open={isRecoveryModalOpen}
        onClose={handleCloseRecoveryModal}
        aria-labelledby="recovery-modal-title"
        aria-describedby="recovery-modal-description"
      >
        <Box style={modalStyle}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Typography id="recovery-modal-title" variant="h6" component="h2">
              Recuperar Senha
            </Typography>
            <IconButton onClick={handleCloseRecoveryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography style={{ marginBottom: 24, fontSize: '0.9rem' }} color="textSecondary">
            Digite seu e-mail para receber um código de verificação
          </Typography>
          
          <form onSubmit={handleRecoverySubmit(handleSendRecoveryCode)}>
            <TextField
              fullWidth
              label="E-mail corporativo"
              variant="outlined"
              margin="normal"
              disabled={isSendingCode}
              error={!!recoveryErrors.login_usuario}
              helperText={recoveryErrors.login_usuario?.message}
              {...registerRecovery('login_usuario')}
              autoComplete="email"
            />
            
            <Box style={{ marginTop: 24, display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <Button
                title="Cancelar"
                variantstyle="RED"
                type="button"
                onClick={handleCloseRecoveryModal}
                disabled={isSendingCode}
                style={{ minWidth: '100px' }}
              />
              <Button
                title={isSendingCode ? "Enviando..." : "Enviar Código"}
                variantstyle="PURPLE"
                type="submit"
                isLoading={isSendingCode}
                disabled={isSendingCode}
                style={{ minWidth: '140px' }}
              />
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Modal para verificação do código */}
      <Modal
        open={isCodeVerificationModalOpen}
        onClose={handleCloseRecoveryModal}
        aria-labelledby="code-modal-title"
      >
        <Box style={modalStyle}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Typography id="code-modal-title" variant="h6" component="h2">
              Verificação de Código
            </Typography>
            <IconButton onClick={handleCloseRecoveryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography style={{ marginBottom: 24, fontSize: '0.9rem' }}>
            Digite o código de 6 dígitos enviado para:
            <br />
            <strong>{recoveryEmail}</strong>
          </Typography>
          
          <form onSubmit={handleCodeSubmit(handleVerifyCode)}>
            <TextField
              fullWidth
              label="Código de verificação"
              variant="outlined"
              margin="normal"
              disabled={isVerifyingCode}
              error={!!codeErrors.codigo}
              helperText={codeErrors.codigo?.message}
              {...registerCode('codigo')}
              inputProps={{ 
                maxLength: 6,
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
              autoComplete="one-time-code"
            />
            
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
              O código é válido por 15 minutos
            </Typography>
            
            <Box style={{ marginTop: 24, display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <Button
                title="Voltar"
                variantstyle="DEFAULT"
                type="button"
                onClick={() => {
                  setIsCodeVerificationModalOpen(false);
                  setIsRecoveryModalOpen(true);
                }}
                disabled={isVerifyingCode}
                style={{ minWidth: '100px' }}
              />
              <Button
                title={isVerifyingCode ? "Verificando..." : "Verificar Código"}
                variantstyle="PURPLE"
                type="submit"
                isLoading={isVerifyingCode}
                disabled={isVerifyingCode}
                style={{ minWidth: '140px' }}
              />
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Modal para nova senha */}
      <Modal
        open={isNewPasswordModalOpen}
        onClose={handleCloseRecoveryModal}
        aria-labelledby="new-password-modal-title"
      >
        <Box style={modalStyle}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Typography id="new-password-modal-title" variant="h6" component="h2">
              Nova Senha
            </Typography>
            <IconButton onClick={handleCloseRecoveryModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography style={{ marginBottom: 24, fontSize: '0.9rem' }}>
            Digite sua nova senha para o e-mail:
            <br />
            <strong>{recoveryEmail}</strong>
          </Typography>
          
          <form onSubmit={handleNewPasswordSubmit(handleUpdatePassword)}>
            <TextField
              fullWidth
              type="password"
              label="Nova senha"
              variant="outlined"
              margin="normal"
              disabled={isUpdatingPassword}
              error={!!newPasswordErrors.nova_senha}
              helperText={newPasswordErrors.nova_senha?.message || "Mínimo 6 caracteres, 1 letra maiúscula e 1 número"}
              {...registerNewPassword('nova_senha')}
              autoComplete="new-password"
            />
            
            <TextField
              fullWidth
              type="password"
              label="Confirmar nova senha"
              variant="outlined"
              margin="normal"
              disabled={isUpdatingPassword}
              error={!!newPasswordErrors.confirmar_senha}
              helperText={newPasswordErrors.confirmar_senha?.message}
              {...registerNewPassword('confirmar_senha')}
              autoComplete="new-password"
            />
            
            <Box style={{ marginTop: 24, display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <Button
                title="Cancelar"
                variantstyle="RED"
                type="button"
                onClick={handleCloseRecoveryModal}
                disabled={isUpdatingPassword}
                style={{ minWidth: '100px' }}
              />
              <Button
                title={isUpdatingPassword ? "Atualizando..." : "Atualizar Senha"}
                variantstyle="PURPLE"
                type="submit"
                isLoading={isUpdatingPassword}
                disabled={isUpdatingPassword}
                style={{ minWidth: '140px' }}
              />
            </Box>
          </form>
        </Box>
      </Modal>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          style={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}